import { useState, useEffect } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Stack,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import useAuthenticatedQuery from "@/hooks/useAuthenticatedQuery";
import useDeleteData from "@/hooks/deleteHooks/useDeleteData";
import usePostJsonData from "@/hooks/postHooks/usePostJsonData";
import Toast from "@/components/toast";
import useApiErrorHandler from "@/hooks/useApiErrorHandler";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSession } from "@/hooks/useSession";
import axios from "@/api/axios";
import AddPrecautionDialog from "./dialogs/AddPrecautionDialog";
import NewDeleteModal from "@/components/newDeleteModal";
import OrganizationHeader from "@/pages/admin/organization/components/OrganizationHeader";
import CategoriesList from "./components/CategoriesList";
import PrecautionsList from "./components/PrecautionsList";
import MobilePrecautionsView from "./components/MobilePrecautionsView";
import AddCategoryDialog from "./components/AddCategoryDialog";
import PrecautionsGuideDialog from "@/components/dialogs/PrecautionsGuideDialog";
import { Security as SecurityIcon } from "@mui/icons-material";

function Precautions() {
  const { t } = useTranslation();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { token } = useSession();
  const { handleApiError, toast, setToast, handleToastClose } =
    useApiErrorHandler();

  // Check if screen is small (mobile/tablet)
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [precautionToDelete, setPrecautionToDelete] = useState(null);
  const [precautionToEdit, setPrecautionToEdit] = useState(null);
  const [addCategoryDialogOpen, setAddCategoryDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [deleteCategoryConfirmOpen, setDeleteCategoryConfirmOpen] =
    useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [helpModalOpen, setHelpModalOpen] = useState(false);

  const { mutate: postPrecaution, isLoading: postIsLoading } =
    usePostJsonData();
  const [editIsLoading, setEditIsLoading] = useState(false);
  const [addCategoryIsLoading, setAddCategoryIsLoading] = useState(false);
  const { mutate: deletePrecaution } = useDeleteData(
    "/api/v1/org-admin/precautions",
    ["precautions"]
  );

  const {
    data: precautions,
    isLoading: precautionsLoading,
    error: errorPrecaution,
  } = useAuthenticatedQuery(["precautions"], "/api/v1/metadata/precautions");

  const {
    data: categories,
    isLoading: categoriesLoading,
    error: errorCategories,
  } = useAuthenticatedQuery(
    ["precautions-categories"],
    "/api/v1/metadata/precautions-categories"
  );

  // Define the preferred order for categories
  const categoryOrder = [
    "Elimination / Substitution Controls",
    "Engineering Controls",
    "Work Method / Task Controls",
    "Administrative Procedural Controls",
    "Personal Protective Equipment Controls",
    "Emergency & Recovery Preparedness",
  ];

  // Group precautions by category - include ALL categories even if they have no precautions
  const groupedPrecautions = {};

  // First, initialize all categories
  categories?.forEach((category) => {
    groupedPrecautions[category.id] = {
      category: category,
      precautions: [],
    };
  });

  // Then, add precautions to their respective categories
  precautions?.forEach((precaution) => {
    const categoryId = precaution.category_id;
    if (groupedPrecautions[categoryId]) {
      groupedPrecautions[categoryId].precautions.push(precaution);
    }
  });

  // Sort categories by preferred order
  const sortedCategoryEntries = Object.entries(groupedPrecautions).sort(
    ([, groupA], [, groupB]) => {
      const nameA = groupA.category?.name || "";
      const nameB = groupB.category?.name || "";

      const indexA = categoryOrder.indexOf(nameA);
      const indexB = categoryOrder.indexOf(nameB);

      // If both categories are in the preferred order, sort by their position
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      // If only one is in the preferred order, prioritize it
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;

      // If neither is in the preferred order, sort alphabetically
      return nameA.localeCompare(nameB);
    }
  );

  // Set default selected category (first one in the sorted list)
  useEffect(() => {
    if (sortedCategoryEntries.length > 0 && !selectedCategoryId) {
      setSelectedCategoryId(sortedCategoryEntries[0][0]);
    }
  }, [sortedCategoryEntries, selectedCategoryId]);

  // Get selected category's precautions
  const selectedCategory = selectedCategoryId
    ? groupedPrecautions[selectedCategoryId]
    : null;

  if (precautionsLoading || categoriesLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress
            size={48}
            aria-label={t("precautions.loadingAria")}
          />
          <Typography variant="body1" color="text.secondary">
            {t("precautions.loadingMessage")}
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (errorPrecaution || errorCategories) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="error" gutterBottom>
          {t("precautions.errorLoading")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("precautions.errorMessage")}
        </Typography>
      </Box>
    );
  }

  const addPrecaution = (precautionName, categoryId, resetFields) => {
    if (precautionName.trim() === "" || !categoryId) return;

    const formattedData = {
      name: precautionName,
      category_id: categoryId,
    };

    postPrecaution(
      { url: "/api/v1/org-admin/precautions", data: formattedData },
      {
        onSuccess: () => {
          resetFields();
          setAddDialogOpen(false);
          setToast({
            open: true,
            message: t("precautions.successAddToast"),
            severity: "success",
          });
          queryClient.invalidateQueries(["precautions"]);
        },
        onError: (err) => {
          handleApiError(err);
        },
      }
    );
  };

  const handleEditPrecaution = (precaution) => {
    setPrecautionToEdit(precaution);
    setEditDialogOpen(true);
  };

  const updatePrecaution = async (precautionName, categoryId, resetFields) => {
    if (precautionName.trim() === "" || !categoryId || !precautionToEdit)
      return;

    const formattedData = {
      name: precautionName,
      category_id: categoryId,
    };

    setEditIsLoading(true);

    try {
      const language = localStorage.getItem("ln") || "en";
      await axios.put(
        `/api/v1/org-admin/precautions/${precautionToEdit.id}`,
        formattedData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "Accept-Language": language,
          },
        }
      );

      resetFields();
      setEditDialogOpen(false);
      setPrecautionToEdit(null);
      setToast({
        open: true,
        message: t("precautions.successEditToast"),
        severity: "success",
      });
      queryClient.invalidateQueries(["precautions"]);
    } catch (err) {
      handleApiError(err);
    } finally {
      setEditIsLoading(false);
    }
  };

  const confirmDeletePrecaution = (precaution) => {
    setPrecautionToDelete(precaution);
    setDeleteConfirmOpen(true);
  };

  const handleDeletePrecaution = () => {
    if (!precautionToDelete) return;

    deletePrecaution(precautionToDelete.id, {
      onSuccess: () => {
        setToast({
          open: true,
          message: t("precautions.successDeleteToast"),
          severity: "success",
        });
        setPrecautionToDelete(null);
        setDeleteConfirmOpen(false);
      },
      onError: (err) => {
        handleApiError(err);
        setPrecautionToDelete(null);
        setDeleteConfirmOpen(false);
      },
    });
  };

  const handleAddCategory = async () => {
    if (newCategoryName.trim() === "") return;

    setAddCategoryIsLoading(true);

    try {
      const language = localStorage.getItem("ln") || "en";
      const response = await axios.post(
        "/api/v1/org-admin/precautions-categories",
        { name: newCategoryName },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "Accept-Language": language,
          },
        }
      );

      setNewCategoryName("");
      setAddCategoryDialogOpen(false);
      setToast({
        open: true,
        message: t("precautions.successAddCategoryToast"),
        severity: "success",
      });

      // Invalidate queries to refetch categories
      await queryClient.invalidateQueries(["precautions-categories"]);

      // Select the newly created category if we have its ID
      if (response.data?.id) {
        setSelectedCategoryId(response.data.id);
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setAddCategoryIsLoading(false);
    }
  };

  const confirmDeleteCategory = (category) => {
    setCategoryToDelete(category);
    setDeleteCategoryConfirmOpen(true);
  };

  const handleDeleteCategory = async () => {
    if (!categoryToDelete) return;

    try {
      const language = localStorage.getItem("ln") || "en";
      await axios.delete(
        `/api/v1/org-admin/precautions-categories/${categoryToDelete.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": language,
          },
        }
      );

      setToast({
        open: true,
        message: t("precautions.successDeleteCategoryToast"),
        severity: "success",
      });

      // If the deleted category was selected, clear the selection
      if (selectedCategoryId === categoryToDelete.id) {
        setSelectedCategoryId(null);
      }

      // Invalidate queries to refetch categories and precautions
      await queryClient.invalidateQueries(["precautions-categories"]);
      await queryClient.invalidateQueries(["precautions"]);

      setCategoryToDelete(null);
      setDeleteCategoryConfirmOpen(false);
    } catch (err) {
      handleApiError(err);
      setCategoryToDelete(null);
      setDeleteCategoryConfirmOpen(false);
    }
  };

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      {/* Compact Enterprise Header */}
      <OrganizationHeader
        icon={SecurityIcon}
        title={t("precautions.headerTitle")}
        subtitle={t("precautions.headerSubtitle")}
        count={precautions?.length || 0}
        onHelpClick={() => setHelpModalOpen(true)}
        helpTooltip={t("precautions.helpTooltip")}
        helpAriaLabel={t("precautions.helpButtonAria")}
      />

      <Box sx={{ px: 4, pb: 4 }}>
        {/* Conditional Layout: Accordion for small screens, Split Panel for large screens */}
        {isSmallScreen ? (
          /* Accordion Layout for Small Screens */
          <MobilePrecautionsView
            sortedCategoryEntries={sortedCategoryEntries}
            onAddCategory={() => setAddCategoryDialogOpen(true)}
            onAddPrecaution={() => setAddDialogOpen(true)}
            onEditPrecaution={handleEditPrecaution}
            onDeletePrecaution={confirmDeletePrecaution}
            onDeleteCategory={confirmDeleteCategory}
            setSelectedCategoryId={setSelectedCategoryId}
          />
        ) : (
          /* Split Panel Layout for Large Screens */
          <Box
            sx={{
              display: "flex",
              gap: 3,
              minHeight: "calc(100vh - 400px)",
            }}
          >
            {/* Left Panel - Categories */}
            <CategoriesList
              sortedCategoryEntries={sortedCategoryEntries}
              selectedCategoryId={selectedCategoryId}
              onSelectCategory={setSelectedCategoryId}
              onAddCategory={() => setAddCategoryDialogOpen(true)}
              onDeleteCategory={confirmDeleteCategory}
            />

            {/* Right Panel - Precautions */}
            <PrecautionsList
              selectedCategory={selectedCategory}
              selectedCategoryId={selectedCategoryId}
              onAddPrecaution={() => setAddDialogOpen(true)}
              onEditPrecaution={handleEditPrecaution}
              onDeletePrecaution={confirmDeletePrecaution}
            />
          </Box>
        )}
      </Box>

      {/* Add Precaution Dialog */}
      <AddPrecautionDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSave={addPrecaution}
        categories={categories}
        categoriesLoading={categoriesLoading}
        isLoading={postIsLoading}
        preSelectedCategoryId={selectedCategoryId}
      />

      {/* Edit Precaution Dialog */}
      <AddPrecautionDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setPrecautionToEdit(null);
        }}
        onSave={updatePrecaution}
        categories={categories}
        categoriesLoading={categoriesLoading}
        isLoading={editIsLoading}
        initialData={precautionToEdit}
        isEditMode={true}
      />

      {/* Delete Precaution Confirmation Dialog */}
      <NewDeleteModal
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDeletePrecaution}
        itemType={t("precautions.itemType")}
      />

      {/* Delete Category Confirmation Dialog */}
      <NewDeleteModal
        open={deleteCategoryConfirmOpen}
        onClose={() => {
          setDeleteCategoryConfirmOpen(false);
          setCategoryToDelete(null);
        }}
        onConfirm={handleDeleteCategory}
        itemType={t("precautions.categoryItemType")}
      />

      {/* Add Category Dialog */}
      <AddCategoryDialog
        open={addCategoryDialogOpen}
        onClose={() => {
          setAddCategoryDialogOpen(false);
          setNewCategoryName("");
        }}
        onSave={handleAddCategory}
        categoryName={newCategoryName}
        onCategoryNameChange={setNewCategoryName}
        isLoading={addCategoryIsLoading}
      />

      {/* Precautions Guide Dialog */}
      <PrecautionsGuideDialog
        open={helpModalOpen}
        onClose={() => setHelpModalOpen(false)}
      />

      {/* Toast Notification */}
      <Toast
        open={toast.open}
        onClose={handleToastClose}
        severity={toast.severity}
        message={toast.message}
      />
    </Box>
  );
}

export default Precautions;
